import Dexie, { type Table } from 'dexie'
import type { HistoryEntry, Template, AnalyticsEvent } from '../types'

export class SupportHelpDB extends Dexie {
  history!: Table<HistoryEntry>
  templates!: Table<Template>
  analytics!: Table<AnalyticsEvent>

  constructor() {
    super('SupportHelpDB')

    this.version(1).stores({
      history: 'id, timestamp',
      templates: 'id, category, usageCount',
      analytics: 'id, type, timestamp',
    })
  }
}

export const db = new SupportHelpDB()

// History operations
export async function addHistoryEntry(entry: HistoryEntry): Promise<void> {
  await db.history.add(entry)
}

export async function getHistoryEntries(limit = 100): Promise<HistoryEntry[]> {
  return db.history.orderBy('timestamp').reverse().limit(limit).toArray()
}

export async function getHistoryEntry(id: string): Promise<HistoryEntry | undefined> {
  return db.history.get(id)
}

export async function deleteHistoryEntry(id: string): Promise<void> {
  await db.history.delete(id)
}

export async function clearHistory(): Promise<void> {
  await db.history.clear()
}

export async function updateHistoryCopied(id: string, platform: string): Promise<void> {
  const entry = await db.history.get(id)
  if (entry && !entry.copiedPlatforms.includes(platform as 'twitter' | 'facebook' | 'forum')) {
    entry.copiedPlatforms.push(platform as 'twitter' | 'facebook' | 'forum')
    await db.history.put(entry)
  }
}

// Template operations
export async function addTemplate(template: Template): Promise<void> {
  await db.templates.add(template)
}

export async function getTemplates(): Promise<Template[]> {
  return db.templates.orderBy('usageCount').reverse().toArray()
}

export async function getTemplatesByCategory(category: string): Promise<Template[]> {
  return db.templates.where('category').equals(category).toArray()
}

export async function updateTemplate(id: string, updates: Partial<Template>): Promise<void> {
  await db.templates.update(id, updates)
}

export async function deleteTemplate(id: string): Promise<void> {
  await db.templates.delete(id)
}

export async function incrementTemplateUsage(id: string): Promise<void> {
  const template = await db.templates.get(id)
  if (template) {
    await db.templates.update(id, { usageCount: template.usageCount + 1 })
  }
}

// Analytics operations
export async function addAnalyticsEvent(event: AnalyticsEvent): Promise<void> {
  await db.analytics.add(event)
}

export async function getAnalyticsEvents(days = 30): Promise<AnalyticsEvent[]> {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - days)
  return db.analytics.where('timestamp').above(cutoff).toArray()
}

export async function clearAnalytics(): Promise<void> {
  await db.analytics.clear()
}
